import { Directive, ViewContainerRef, ContentChildren, ContentChild } from '@angular/core';
import { ComplexBase, ArrayBase, setValue } from '@syncfusion/ej2-angular-base';
import { Template } from '@syncfusion/ej2-angular-base';


let /** @type {?} */ input: string[] = ['allowEditing', 'allowFiltering', 'allowGrouping', 'allowReordering', 'allowResizing', 'allowSearching', 'allowSorting', 'autoFit', 'clipMode', 'columns', 'commands', 'customAttributes', 'dataSource', 'defaultValue', 'disableHtmlEncode', 'displayAsCheckBox', 'edit', 'editTemplate', 'editType', 'enableGroupByFormat', 'field', 'filter', 'filterBarTemplate', 'filterTemplate', 'foreignKeyField', 'foreignKeyValue', 'format', 'formatter', 'freeze', 'headerTemplate', 'headerText', 'headerTextAlign', 'headerValueAccessor', 'hideAtMedia', 'index', 'isFrozen', 'isIdentity', 'isPrimaryKey', 'lockColumn', 'maxWidth', 'minWidth', 'showColumnMenu', 'showInColumnChooser', 'sortComparer', 'template', 'textAlign', 'type', 'uid', 'validationRules', 'valueAccessor', 'visible', 'width'];
let /** @type {?} */ outputs: string[] = [];
/**
 * `e-column` directive represent a column of the Angular Grid. 
 * It must be contained in a Grid component(`ejs-grid`). 
 * ```html
 * <ejs-grid [dataSource]='data' allowPaging='true' allowSorting='true'> 
 *   <e-columns>
 *    <e-column field='ID' width='100'></e-column>
 *    <e-column field='name' headerText='Name' width='100'></e-column>
 *   </e-columns>
 * </ejs-grid>
 * ```
 */
export class ColumnDirective extends ComplexBase<ColumnDirective> {
public directivePropList: any;
/**
 * Defines the data type of the column.
 * \@default null
 * \@blazortype ColumnType
 */
public type: any;
/**
 * If `allowEditing` set to false, then it disables editing of a particular column. 
 * By default all columns are editable.
 * \@default true
 */
public allowEditing: any;
/**
 * If `allowFiltering` set to false, then it disables filtering option and filter bar element of a particular column. 
 * By default all columns are filterable.
 * \@default true
 */
public allowFiltering: any;
/**
 * If `allowGrouping` set to false, then it disables grouping of a particular column. 
 * By default all columns are groupable.
 * \@default true
 */
public allowGrouping: any;
/**
 * If `allowReordering` set to false, then it disables reorder of a particular column. 
 * By default all columns can be reorder.
 * \@default true
 */
public allowReordering: any;
/**
 * If `allowResizing` set to false, it disables resize option of a particular column.
 * \@default true
 */
public allowResizing: any;
/**
 * If `allowSearching` set to false, then it disables Searching of a particular column. 
 * By default all columns allow Searching.
 * \@default true
 */
public allowSearching: any;
/**
 * If `allowSorting` set to false, then it disables sorting option of a particular column. 
 * By default all columns are sortable.
 * \@default true
 */
public allowSorting: any;
/**
 * If `autoFit` set to true, then the particular column content width will be 
 * adjusted based on its content in the initial rendering itself. 
 * Setting this property as true is equivalent to calling `autoFitColumns` method in the `dataBound` event.
 * \@default false
 */
public autoFit: any;
/**
 * Defines the cell content's overflow mode. The available modes are 
 * * `Clip` -  Truncates the cell content when it overflows its area. 
 * * `Ellipsis` -  Displays ellipsis when the cell content overflows its area. 
 * * `EllipsisWithTooltip` - Displays ellipsis when the cell content overflows its area 
 * also it will display tooltip while hover on ellipsis applied cell.
 * \@default Ellipsis
 */
public clipMode: any;
/**
 * Used to render multiple header rows(stacked headers) on the Grid header.
 * \@default null
 */
public columns: any;
/**
 * `commands` provides an option to display command buttons in every cell. 
 * The available built-in command buttons are 
 * * Edit - Edit the record. 
 * * Delete - Delete the record. 
 * * Save - Save the record. 
 * * Cancel - Cancel the edit state.
 * 
 * The following code example implements the custom command column.
 * ```html
 * <style type="text/css" class="cssStyles">
 * .details-icon:before
 * {
 *   content:"\e74d";
 * }
 * </style>
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * var gridObj = new Grid({
 * datasource: window.gridData,
 * columns : [
 * { field: 'CustomerID', headerText: 'Customer ID' },
 * { field: 'CustomerName', headerText: 'Customer Name' },
 * {commands: [{buttonOption:{content: 'Details', click: onClick, cssClass: details-icon}}], headerText: 'Customer Details'}
 * ]
 * gridObj.appendTo("#Grid");
 * ```     
 * \@default null
 */
public commands: any;
/**
 * The CSS styles and attributes of the content cells of a particular column can be customized.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: filterData,
 * columns: [
 *   { field: 'OrderID', headerText: 'Order ID' },
 *   {
 *       field: 'EmployeeID', headerText: 'Employee ID', customAttributes: {
 *          class: 'employeeid',
 *          type: 'employee-id-cell'
 *     }
 *  }]
 * });
 * gridObj.appendTo('#Grid');
 * ```
 *     
 * \@default null
 */
public customAttributes: any;
/**
 * Defines the column data source  which will act as foreign data source.
 * \@default null
 */
public dataSource: any;
/**
 * Defines default values for the component when adding a new record to the Grid.
 * \@default null
 * \@asptype object
 * \@blazortype object
 */
public defaultValue: any;
/**
 * If `disableHtmlEncode` is set to true, it encodes the HTML of the header and content cells.
 * \@default true
 */
public disableHtmlEncode: any;
/**
 * If `displayAsCheckBox` is set to true, it displays the column value as a check box instead of Boolean value.
 * \@default false
 */
public displayAsCheckBox: any;
/**
 * Defines the `IEditCell`(../../grid/edit/#cell-edit-template) object to customize default edit cell.
 * \@default {}
 */
public edit: any;
/**
 * Defines the type of component for editing.
 * \@default 'stringedit'
 * \@blazortype EditType
 * \@blazordefaultvalue EditType.DefaultEdit
 */
public editType: any;
/**
 * If `enableGroupByFormat` set to true, then it groups the particular column by formatted values. 
 * By default no columns are group by format.
 * \@default true
 */
public enableGroupByFormat: any;
/**
 * Defines the field name of column which is mapped with mapping name of DataSource. 
 * The bounded columns can be sort, filter and group etc., 
 * If the `field` name contains “dot”, then it is considered as complex binding. 
 * The `field` name must be a valid JavaScript identifier, 
 * the first character must be an alphabet and should not contain spaces and special characters.
 * \@default ''
 */
public field: any;
/**
 *  Defines the filter options to customize filtering for the particular column. 
 *  \@default null
 */
public filter: any;
/**
 * The `filterBarTemplate` is used to add a custom component instead of default input component for filter bar. 
 * It have create and read functions. 
 * * create: It is used for creating custom components. 
 * * read: It is used to perform custom filter action.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: filterData,
 * columns: [
 *  { field: 'OrderID', headerText: 'Order ID' },
 *  {
 *     field: 'EmployeeID', filterBarTemplate: {
 *        create: (args: { element: Element, column: Column }) => {
 *             let input: HTMLInputElement = document.createElement('input');
 *             input.id = 'EmployeeID';
 *             input.type = 'text';
 *             return input;
 *        },
 *        write: (args: { element: Element, column: Column }) => {
 *            args.element.addEventListener('input', args.column.filterBarTemplate.read as EventListener);
 *        },
 *        read: (args: { element: HTMLInputElement, columnIndex: number, column: Column }) => {
 *            gridObj.filterByColumn(args.element.id, 'equal', args.element.value);
 *       }
 *    }
 * }],
 *  allowFiltering: true
 * });
 * gridObj.appendTo('#Grid');
 * ```
 *     
 * \@default null
 */
public filterBarTemplate: any;
/**
 * Defines the mapping column name of the foreign data source. 
 * If it is not defined then the `columns.field` will be considered as mapping column name
 * \@default null
 */
public foreignKeyField: any;
/**
 * Defines the display column name from the foreign data source which will be obtained from comparing local and foreign data
 * \@default null
 */
public foreignKeyValue: any;
/**
 * It is used to change display value with the given format and does not affect the original data. 
 * Gets the format from the user which can be standard or custom 
 * [`number`](../../common/internationalization/#manipulating-numbers) 
 * and [`date`](../../common/internationalization/#manipulating-datetime) formats.
 * \@default null
 * \@asptype string
 * \@blazortype string
 */
public format: any;
/**
 * Defines the method which is used to achieve custom formatting from an external function. 
 * This function triggers before rendering of each cell.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * class ExtendedFormatter implements ICellFormatter {
 * public getValue(column: Column, data: Object): Object {
 *  return '<span style="color:' + (data['Verified'] ? 'green' : 'red') + '"><i>' + data['Verified'] + '</i><span>';
 * }
 * }
 * let gridObj: Grid = new Grid({
 *    dataSource: filterData,
 *    columns: [
 *        { field: 'ShipName', headerText: 'Ship Name' },
 *        { field: 'Verified', headerText: 'Verified Status', formatter: ExtendedFormatter }]
 * });
 * gridObj.appendTo('#Grid');
 * ```
 *     
 * \@default null
 */
public formatter: any;
public freeze: any;
/**
 * Defines the header text of column which is used to display in column header. 
 * If `headerText` is not defined, then field name value will be assigned to header text.
 * \@default null
 */
public headerText: any;
/**
 * Define the alignment of column header which is used to align the text of column header.
 * \@aspdefaultvalueignore
 * \@blazordefaultvalueignore
 * \@default null
 */
public headerTextAlign: any;
/**
 * Defines the method used to apply custom header cell values from external function and display this on each cell rendered.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: [{ EmployeeID: 1, EmployeeName: ['John', 'M'] }, { EmployeeID: 2, EmployeeName: ['Peter', 'A'] }],
 * columns: [
 *    { field: 'EmployeeID', headerText: 'Employee ID' },
 *    { field: 'EmployeeName', headerText: 'Employee First Name',
 *      headerValueAccessor: (field: string,column: Column) => {
 *            return "newheadername";
 *        },
 *    }]
 * });
 * ```
 *     
 * \@default null
 * @deprecated
 */
public headerValueAccessor: any;
/**
 * column visibility can change based on its [`Media Queries`](http://cssmediaqueries.com/what-are-css-media-queries.html). 
 * `hideAtMedia` accepts only valid Media Queries.
 * \@default ''
 */
public hideAtMedia: any;
/**
 * Gets the unique identifier value of the column. It is used to get the object.
 * \@default null
 */
public index: any;
/**
 * You can use this property to freeze selected columns in grid.
 * \@default false
 */
public isFrozen: any;
/**
 * If `isIdentity` is set to true, then this column is considered as identity column.
 * \@default false
 */
public isIdentity: any;
/**
 * If `isPrimaryKey` is set to true, considers this column as the primary key constraint.
 * \@default false
 */
public isPrimaryKey: any;
/**
 * Defines the mapping column name of the foreign data source. 
 * If it is not defined then the `columns.field` will be considered as mapping column name
 * \@default false
 */
public lockColumn: any;
/**
 * Defines the maximum width of the column in pixel or percentage, which will restrict resizing beyond this pixel or percentage.
 * \@default ''
 */
public maxWidth: any;
/**
 * Defines the minimum width of the column in pixels or percentage.
 * \@default ''
 */
public minWidth: any;
/**
 * If `showColumnMenu` set to false, then it disable the column menu of a particular column. 
 * By default column menu will show for all columns
 * \@default true
 */
public showColumnMenu: any;
/**
 * If `showInColumnChooser` set to false, then hides the particular column in column chooser. 
 * By default all columns are displayed in column Chooser.
 * \@default true
 */
public showInColumnChooser: any;
/**
 * It defines the custom sort comparer function.
 */
public sortComparer: any;
/**
 * Defines the alignment of the column in both header and content cells.
 * \@default Left
 */
public textAlign: any;
/**
 * Gets the unique identifier value of the column. It is used to get the object.
 * \@default ''
 */
public uid: any;
/**
 * `editType`(../../grid/edit/#cell-edit-type-and-its-params) Defines rules to validate data before creating and updating.
 * \@default null
 */
public validationRules: any;
/**
 * Defines the method used to apply custom cell values from external function and display this on each cell rendered.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: [{ EmployeeID: 1, EmployeeName: ['John', 'M'] }, { EmployeeID: 2, EmployeeName: ['Peter', 'A'] }],
 * columns: [
 *    { field: 'EmployeeID', headerText: 'Employee ID' },
 *    { field: 'EmployeeName', headerText: 'Employee First Name',
 *      valueAccessor: (field: string, data: Object, column: Column) => {
 *            return data['EmployeeName'][0];
 *        },
 *    }]
 * });
 * ```
 *     
 * \@default null
 */
public valueAccessor: any;
/**
 * If `visible` is set to false, hides the particular column. By default, all columns are displayed.
 * \@default true
 */
public visible: any;
/**
 * Defines the width of the column in pixels or percentage.
 * \@default ''
 */
public width: any;
/**
 * Defines the column template that renders customized element in each cell of the column. 
 * It accepts either [template string](../../common/template-engine/) or HTML element ID.
 * \@default null
 */
@Template()
    public template: any;
/**
 * Defines the column template as string or HTML element ID which is used to add customized element in the column header.
 * \@default null
 */
@Template()
    public headerTemplate: any;
    
    @Template()
public commandsTemplate: any;
    
    @Template()
public filter_itemTemplate: any;
/**
 * Defines the cell edit template that used as editor for a particular column. 
 * It accepts either template string or HTML element ID.
 * \@aspignore
 */
@Template()
    public editTemplate: any;
/**
 * Defines the filter template/UI that used as filter for a particular column. 
 * It accepts either template string or HTML element ID.
 * \@aspignore
 */
@Template()
    public filterTemplate: any;
/**
 * @param {?} viewContainerRef
 */
constructor(private viewContainerRef:ViewContainerRef) {
        super();
        setValue('currentInstance', this, this.viewContainerRef);
        this.registerEvents(outputs);
        this.directivePropList = input;
    }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
    selector: 'ejs-grid>e-columns>e-column',
    inputs: input,
    outputs: outputs,    
    queries: {

    }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ViewContainerRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'template': [{ type: ContentChild, args: ['template', ] },],
'headerTemplate': [{ type: ContentChild, args: ['headerTemplate', ] },],
'commandsTemplate': [{ type: ContentChild, args: ['commandsTemplate', ] },],
'filter_itemTemplate': [{ type: ContentChild, args: ['filterItemTemplate', ] },],
'editTemplate': [{ type: ContentChild, args: ['editTemplate', ] },],
'filterTemplate': [{ type: ContentChild, args: ['filterTemplate', ] },],
};
}

function ColumnDirective_tsickle_Closure_declarations() {
/** @type {?} */
ColumnDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ColumnDirective.ctorParameters;
/** @type {?} */
ColumnDirective.propDecorators;
/** @type {?} */
ColumnDirective.prototype.directivePropList;
/**
 * Defines the data type of the column.
 * \@default null
 * \@blazortype ColumnType
 * @type {?}
 */
ColumnDirective.prototype.type;
/**
 * If `allowEditing` set to false, then it disables editing of a particular column. 
 * By default all columns are editable.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowEditing;
/**
 * If `allowFiltering` set to false, then it disables filtering option and filter bar element of a particular column. 
 * By default all columns are filterable.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowFiltering;
/**
 * If `allowGrouping` set to false, then it disables grouping of a particular column. 
 * By default all columns are groupable.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowGrouping;
/**
 * If `allowReordering` set to false, then it disables reorder of a particular column. 
 * By default all columns can be reorder.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowReordering;
/**
 * If `allowResizing` set to false, it disables resize option of a particular column.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowResizing;
/**
 * If `allowSearching` set to false, then it disables Searching of a particular column. 
 * By default all columns allow Searching.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowSearching;
/**
 * If `allowSorting` set to false, then it disables sorting option of a particular column. 
 * By default all columns are sortable.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.allowSorting;
/**
 * If `autoFit` set to true, then the particular column content width will be 
 * adjusted based on its content in the initial rendering itself. 
 * Setting this property as true is equivalent to calling `autoFitColumns` method in the `dataBound` event.
 * \@default false
 * @type {?}
 */
ColumnDirective.prototype.autoFit;
/**
 * Defines the cell content's overflow mode. The available modes are 
 * * `Clip` -  Truncates the cell content when it overflows its area. 
 * * `Ellipsis` -  Displays ellipsis when the cell content overflows its area. 
 * * `EllipsisWithTooltip` - Displays ellipsis when the cell content overflows its area 
 * also it will display tooltip while hover on ellipsis applied cell.
 * \@default Ellipsis
 * @type {?}
 */
ColumnDirective.prototype.clipMode;
/**
 * Used to render multiple header rows(stacked headers) on the Grid header.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.columns;
/**
 * `commands` provides an option to display command buttons in every cell. 
 * The available built-in command buttons are 
 * * Edit - Edit the record. 
 * * Delete - Delete the record. 
 * * Save - Save the record. 
 * * Cancel - Cancel the edit state.
 * 
 * The following code example implements the custom command column.
 * ```html
 * <style type="text/css" class="cssStyles">
 * .details-icon:before
 * {
 *   content:"\e74d";
 * }
 * </style>
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * var gridObj = new Grid({
 * datasource: window.gridData,
 * columns : [
 * { field: 'CustomerID', headerText: 'Customer ID' },
 * { field: 'CustomerName', headerText: 'Customer Name' },
 * {commands: [{buttonOption:{content: 'Details', click: onClick, cssClass: details-icon}}], headerText: 'Customer Details'}
 * ]
 * gridObj.appendTo("#Grid");
 * ```     
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.commands;
/**
 * The CSS styles and attributes of the content cells of a particular column can be customized.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: filterData,
 * columns: [
 *   { field: 'OrderID', headerText: 'Order ID' },
 *   {
 *       field: 'EmployeeID', headerText: 'Employee ID', customAttributes: {
 *          class: 'employeeid',
 *          type: 'employee-id-cell'
 *     }
 *  }]
 * });
 * gridObj.appendTo('#Grid');
 * ```
 *     
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.customAttributes;
/**
 * Defines the column data source  which will act as foreign data source.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.dataSource;
/**
 * Defines default values for the component when adding a new record to the Grid.
 * \@default null
 * \@asptype object
 * \@blazortype object
 * @type {?}
 */
ColumnDirective.prototype.defaultValue;
/**
 * If `disableHtmlEncode` is set to true, it encodes the HTML of the header and content cells.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.disableHtmlEncode;
/**
 * If `displayAsCheckBox` is set to true, it displays the column value as a check box instead of Boolean value.
 * \@default false
 * @type {?}
 */
ColumnDirective.prototype.displayAsCheckBox;
/**
 * Defines the `IEditCell`(../../grid/edit/#cell-edit-template) object to customize default edit cell.
 * \@default {}
 * @type {?}
 */
ColumnDirective.prototype.edit;
/**
 * Defines the type of component for editing.
 * \@default 'stringedit'
 * \@blazortype EditType
 * \@blazordefaultvalue EditType.DefaultEdit
 * @type {?}
 */
ColumnDirective.prototype.editType;
/**
 * If `enableGroupByFormat` set to true, then it groups the particular column by formatted values. 
 * By default no columns are group by format.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.enableGroupByFormat;
/**
 * Defines the field name of column which is mapped with mapping name of DataSource. 
 * The bounded columns can be sort, filter and group etc., 
 * If the `field` name contains “dot”, then it is considered as complex binding. 
 * The `field` name must be a valid JavaScript identifier, 
 * the first character must be an alphabet and should not contain spaces and special characters.
 * \@default ''
 * @type {?}
 */
ColumnDirective.prototype.field;
/**
 *  Defines the filter options to customize filtering for the particular column. 
 *  \@default null
 * @type {?}
 */
ColumnDirective.prototype.filter;
/**
 * The `filterBarTemplate` is used to add a custom component instead of default input component for filter bar. 
 * It have create and read functions. 
 * * create: It is used for creating custom components. 
 * * read: It is used to perform custom filter action.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: filterData,
 * columns: [
 *  { field: 'OrderID', headerText: 'Order ID' },
 *  {
 *     field: 'EmployeeID', filterBarTemplate: {
 *        create: (args: { element: Element, column: Column }) => {
 *             let input: HTMLInputElement = document.createElement('input');
 *             input.id = 'EmployeeID';
 *             input.type = 'text';
 *             return input;
 *        },
 *        write: (args: { element: Element, column: Column }) => {
 *            args.element.addEventListener('input', args.column.filterBarTemplate.read as EventListener);
 *        },
 *        read: (args: { element: HTMLInputElement, columnIndex: number, column: Column }) => {
 *            gridObj.filterByColumn(args.element.id, 'equal', args.element.value);
 *       }
 *    }
 * }],
 *  allowFiltering: true
 * });
 * gridObj.appendTo('#Grid');
 * ```
 *     
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.filterBarTemplate;
/**
 * Defines the mapping column name of the foreign data source. 
 * If it is not defined then the `columns.field` will be considered as mapping column name
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.foreignKeyField;
/**
 * Defines the display column name from the foreign data source which will be obtained from comparing local and foreign data
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.foreignKeyValue;
/**
 * It is used to change display value with the given format and does not affect the original data. 
 * Gets the format from the user which can be standard or custom 
 * [`number`](../../common/internationalization/#manipulating-numbers) 
 * and [`date`](../../common/internationalization/#manipulating-datetime) formats.
 * \@default null
 * \@asptype string
 * \@blazortype string
 * @type {?}
 */
ColumnDirective.prototype.format;
/**
 * Defines the method which is used to achieve custom formatting from an external function. 
 * This function triggers before rendering of each cell.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * class ExtendedFormatter implements ICellFormatter {
 * public getValue(column: Column, data: Object): Object {
 *  return '<span style="color:' + (data['Verified'] ? 'green' : 'red') + '"><i>' + data['Verified'] + '</i><span>';
 * }
 * }
 * let gridObj: Grid = new Grid({
 *    dataSource: filterData,
 *    columns: [
 *        { field: 'ShipName', headerText: 'Ship Name' },
 *        { field: 'Verified', headerText: 'Verified Status', formatter: ExtendedFormatter }]
 * });
 * gridObj.appendTo('#Grid');
 * ```
 *     
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.formatter;
/** @type {?} */
ColumnDirective.prototype.freeze;
/**
 * Defines the header text of column which is used to display in column header. 
 * If `headerText` is not defined, then field name value will be assigned to header text.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.headerText;
/**
 * Define the alignment of column header which is used to align the text of column header.
 * \@aspdefaultvalueignore
 * \@blazordefaultvalueignore
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.headerTextAlign;
/**
 * Defines the method used to apply custom header cell values from external function and display this on each cell rendered.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: [{ EmployeeID: 1, EmployeeName: ['John', 'M'] }, { EmployeeID: 2, EmployeeName: ['Peter', 'A'] }],
 * columns: [
 *    { field: 'EmployeeID', headerText: 'Employee ID' },
 *    { field: 'EmployeeName', headerText: 'Employee First Name',
 *      headerValueAccessor: (field: string,column: Column) => {
 *            return "newheadername";
 *        },
 *    }]
 * });
 * ```
 *     
 * \@default null
 * @deprecated
 * @type {?}
 */
ColumnDirective.prototype.headerValueAccessor;
/**
 * column visibility can change based on its [`Media Queries`](http://cssmediaqueries.com/what-are-css-media-queries.html). 
 * `hideAtMedia` accepts only valid Media Queries.
 * \@default ''
 * @type {?}
 */
ColumnDirective.prototype.hideAtMedia;
/**
 * Gets the unique identifier value of the column. It is used to get the object.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.index;
/**
 * You can use this property to freeze selected columns in grid.
 * \@default false
 * @type {?}
 */
ColumnDirective.prototype.isFrozen;
/**
 * If `isIdentity` is set to true, then this column is considered as identity column.
 * \@default false
 * @type {?}
 */
ColumnDirective.prototype.isIdentity;
/**
 * If `isPrimaryKey` is set to true, considers this column as the primary key constraint.
 * \@default false
 * @type {?}
 */
ColumnDirective.prototype.isPrimaryKey;
/**
 * Defines the mapping column name of the foreign data source. 
 * If it is not defined then the `columns.field` will be considered as mapping column name
 * \@default false
 * @type {?}
 */
ColumnDirective.prototype.lockColumn;
/**
 * Defines the maximum width of the column in pixel or percentage, which will restrict resizing beyond this pixel or percentage.
 * \@default ''
 * @type {?}
 */
ColumnDirective.prototype.maxWidth;
/**
 * Defines the minimum width of the column in pixels or percentage.
 * \@default ''
 * @type {?}
 */
ColumnDirective.prototype.minWidth;
/**
 * If `showColumnMenu` set to false, then it disable the column menu of a particular column. 
 * By default column menu will show for all columns
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.showColumnMenu;
/**
 * If `showInColumnChooser` set to false, then hides the particular column in column chooser. 
 * By default all columns are displayed in column Chooser.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.showInColumnChooser;
/**
 * It defines the custom sort comparer function.
 * @type {?}
 */
ColumnDirective.prototype.sortComparer;
/**
 * Defines the alignment of the column in both header and content cells.
 * \@default Left
 * @type {?}
 */
ColumnDirective.prototype.textAlign;
/**
 * Gets the unique identifier value of the column. It is used to get the object.
 * \@default ''
 * @type {?}
 */
ColumnDirective.prototype.uid;
/**
 * `editType`(../../grid/edit/#cell-edit-type-and-its-params) Defines rules to validate data before creating and updating.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.validationRules;
/**
 * Defines the method used to apply custom cell values from external function and display this on each cell rendered.
 * 
 * ```html
 * <div id="Grid"></div>
 * ```
 * ```typescript
 * let gridObj: Grid = new Grid({
 * dataSource: [{ EmployeeID: 1, EmployeeName: ['John', 'M'] }, { EmployeeID: 2, EmployeeName: ['Peter', 'A'] }],
 * columns: [
 *    { field: 'EmployeeID', headerText: 'Employee ID' },
 *    { field: 'EmployeeName', headerText: 'Employee First Name',
 *      valueAccessor: (field: string, data: Object, column: Column) => {
 *            return data['EmployeeName'][0];
 *        },
 *    }]
 * });
 * ```
 *     
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.valueAccessor;
/**
 * If `visible` is set to false, hides the particular column. By default, all columns are displayed.
 * \@default true
 * @type {?}
 */
ColumnDirective.prototype.visible;
/**
 * Defines the width of the column in pixels or percentage.
 * \@default ''
 * @type {?}
 */
ColumnDirective.prototype.width;
/**
 * Defines the column template that renders customized element in each cell of the column. 
 * It accepts either [template string](../../common/template-engine/) or HTML element ID.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.template;
/**
 * Defines the column template as string or HTML element ID which is used to add customized element in the column header.
 * \@default null
 * @type {?}
 */
ColumnDirective.prototype.headerTemplate;
/** @type {?} */
ColumnDirective.prototype.commandsTemplate;
/** @type {?} */
ColumnDirective.prototype.filter_itemTemplate;
/**
 * Defines the cell edit template that used as editor for a particular column. 
 * It accepts either template string or HTML element ID.
 * \@aspignore
 * @type {?}
 */
ColumnDirective.prototype.editTemplate;
/**
 * Defines the filter template/UI that used as filter for a particular column. 
 * It accepts either template string or HTML element ID.
 * \@aspignore
 * @type {?}
 */
ColumnDirective.prototype.filterTemplate;
/** @type {?} */
ColumnDirective.prototype.viewContainerRef;
}

/**
 * Column Array Directive
 */
export class ColumnsDirective extends ArrayBase<ColumnsDirective> {
constructor() {
        super('columns');
    }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
    selector: 'ejs-grid>e-columns',
    queries: {
        children: new ContentChildren(ColumnDirective)
    },
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function ColumnsDirective_tsickle_Closure_declarations() {
/** @type {?} */
ColumnsDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ColumnsDirective.ctorParameters;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
